// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import Section from '@/components/sections/Section'
import SectionHeader from '@/components/sections/SectionHeader'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img
import StackImage from 'public/img/products/Air1000/from_machine_to_cloud.webp'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody } from '@/assets/theme/fonts'

export default function StackSection() {
  const { t } = useTranslation('index')
  const { screenDimensions } = useContext(GlobalContext)

  const stacked = screenDimensions.width < breakpoints.desktopToMobile

  const steps = [t('stack.steps.01'), t('stack.steps.02'), t('stack.steps.03')]

  const getContent = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: stacked ? '1fr' : '1fr minmax(0, 26rem)',
        gap: stacked ? '2rem' : '3rem',
        alignItems: 'center',
        width: '100%'
      }}
    >
      <Box>
        <SectionHeader
          eyebrow={t('stack.eyebrow')}
          title={t('stack.title')}
          intro={[t('stack.p1'), t('stack.p2'), t('stack.p3')]}
        />

        {/* The escalation reads as three beats, so each gets its own line. */}
        <Box sx={{ marginBottom: '1.75rem' }}>
          {steps.map((step, idx) => (
            <Box
              key={step}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.9rem',
                paddingLeft: `${idx * 1.25}rem`,
                marginBottom: '0.75rem'
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: '0.5rem',
                  height: '0.5rem',
                  borderRadius: '50%',
                  marginTop: '0.6rem',
                  background: themeColors.success.main
                }}
              />
              <Typography
                component="p"
                fontSize={getFontBody()}
                sx={{ lineHeight: 1.7, color: themeColors.gray.defaultText }}
              >
                {step}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography
          component="p"
          fontSize={getFontBody()}
          sx={{
            paddingLeft: '1rem',
            borderLeft: `3px solid ${themeColors.success.main}`,
            lineHeight: 1.7,
            fontWeight: 500,
            color: themeColors.gray.dark
          }}
        >
          {t('stack.note')}
        </Typography>
      </Box>

      <Box
        sx={{
          lineHeight: 0,
          borderRadius: '10px',
          overflow: 'hidden',
          order: stacked ? -1 : 0
        }}
      >
        <Image
          src={StackImage}
          alt="Data path from a machine through the network to the cloud"
          sizes="(max-width: 990px) 100vw, 40vw"
          style={{
            width: '100%',
            height: 'auto',
            maxWidth: StackImage.width
          }}
        />
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.kroemtech.background}
      paddingTop="5rem"
      paddingBottom="5rem"
      content={getContent()}
    />
  )
}
